import React, { createContext, useContext, useState, useOptimistic, useMemo, ReactNode } from 'react';
import { Store } from '../types/Store';
import useWorkspaceChange from '../hooks/useWorkspaceChange';

interface OptimisticStoreContextType {
    displayStore: Store | null;
    actualStore: Store | null;
    setActualStore: (store: Store | null) => void;
}

const OptimisticStoreContext = createContext<OptimisticStoreContextType | undefined>(undefined);

export const OptimisticStoreProvider = ({ children }: { children: ReactNode }) => {
    const [actualStore, setActualStore] = useState<Store | null>(null);
    const [displayStore] = useOptimistic(actualStore, (currentStore: Store | null) => currentStore);

    // Réinitialiser le magasin sélectionné au changement de workspace pour éviter toute fuite de données
    useWorkspaceChange(() => { setActualStore(null); });

    const value = useMemo(() => ({ displayStore, actualStore, setActualStore }), [displayStore, actualStore]);

    return (
        <OptimisticStoreContext.Provider value={value}>
            {children}
        </OptimisticStoreContext.Provider>
    );
};

export const useOptimisticStore = () => {
    const context = useContext(OptimisticStoreContext);
    if (!context) {
        throw new Error('useOptimisticStore must be used within OptimisticStoreProvider');
    }
    return context;
};
